import { useDateValidationProps } from "@judo/core";
import { useModelLabel } from "@judo/i18n";
import type { DateTimeInput, Input } from "@judo/model-api";
import { getInputTestId } from "@judo/test-ids";
import type { TextFieldProps } from "@mui/material/TextField";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { isValid, parseISO } from "date-fns";
import { buildIconAdornment } from "../utils/icon-adornment";

export interface DateTimeInputComponentProps {
	element: DateTimeInput | Input;
	readOnly?: boolean;
	value?: Date | string | null;
	onChange?: (value: Date | null) => void;
	error?: string;
	disabled?: boolean;
	judoClassName?: string;
}

/**
 * Parse a string value to a Date. Handles ISO 8601 strings via `parseISO`.
 */
function parseToDate(value: Date | string | null | undefined): Date | null {
	if (value == null) return null;
	if (value instanceof Date) return isValid(value) ? value : null;
	const parsed = parseISO(value);
	return isValid(parsed) ? parsed : null;
}

/**
 * DateTime input component using MUI X DateTimePicker.
 */
export function DateTimeInputComponent({
	element,
	readOnly,
	value,
	onChange,
	error,
	disabled,
	judoClassName,
}: DateTimeInputComponentProps) {
	const testId = getInputTestId(element);
	const { label } = useModelLabel(element);
	const dateValidation = useDateValidationProps(element["xmi:id"]);
	const startAdornment = buildIconAdornment(element.icon);

	// Resolve min/max from DateValidationProps — explicit precedence:
	// an explicit min/maxDate always wins over disablePast/disableFuture.
	const today = new Date();
	const minDateTime = dateValidation?.minDate ?? (dateValidation?.disablePast ? today : undefined);
	const maxDateTime = dateValidation?.maxDate ?? (dateValidation?.disableFuture ? today : undefined);

	return (
		<DateTimePicker
			label={label}
			views={["year", "month", "day", "hours", "minutes", "seconds"]}
			className={judoClassName}
			ampm={false}
			ampmInClock={false}
			value={parseToDate(value)}
			onChange={(newValue) => onChange?.(newValue)}
			disabled={disabled ?? element.disabled}
			readOnly={readOnly ?? element.isReadOnly}
			minDateTime={minDateTime}
			maxDateTime={maxDateTime}
			slotProps={{
				// Cast: MUI X's slot type strips arbitrary attributes, but `data-testid`
				// is a valid HTML attribute on the underlying TextField root.
				textField: {
					"data-testid": testId,
					error: !!error,
					helperText: error,
					required: element.required,
					fullWidth: true,
					slotProps: {
						input: {
							startAdornment,
						},
						inputLabel: {
							shrink: true,
						},
					},
				} as TextFieldProps,
				actionBar: {
					actions: ["today", "accept"],
				},
			}}
		/>
	);
}
